import { useMemo } from 'react';
import getLang from 'LearningCenterUI/utils/getLang';
import { List } from 'foundations-components/transitional/components/List';
import UIRouterButton from 'ui-addon-react-router-dom/UIRouterButton';
import FormattedMessage from 'I18n/components/FormattedMessage';
// @ts-expect-error untyped dependency
import FormattedDuration from 'I18n/components/FormattedDuration';
import UsageTracker from '../../utils/UsageTracker';
import { FILTER_TYPES } from '../../constants/TopFilterConstants';
import { CardMenuIcon } from '../../features/cardMenu';
import { useAvailableLanguagesByTrack, useFullTrack } from '../../features/tracks';
// @ts-expect-error Could not find a declaration file for module
import { useSearchParam } from '../../hooks/searchParamHooks';
import ACCertificatePreviewSection from 'academy-common-lib/components/section/ACCertificatePreviewSection';
import InstructorList from '../../components/InstructorList';
import { OverviewContent } from '../../features/overview/components/OverviewContent';
import { OverviewModalCurriculumSection } from '../../features/overview/components/OverviewModalCurriculumSection';
import { OverviewModalDescriptionSection } from '../../features/overview/components/OverviewModalDescriptionSection';
import { useOverviewNavigation } from '../../features/overview/overviewRouting';
import { ExpandableSection } from 'foundations-components/transitional/components/ExpandableSection';
import LearningContentCard from '../../features/cards/components/LearningContentCard';
import { lessonToLessonCardModel } from '../../features/lessons/lessonUtils';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { View } from 'foundations-components/transitional/components/View';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import styled from 'foundations-theming/utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ExpandableSectionWrapper = styled.div.withConfig({
  displayName: "TrackOverviewModalContainer__ExpandableSectionWrapper",
  componentId: "sc-1a2k9hm-0"
})(["div{border-top:none;}[role='presentation']{width:100%;}"]);
const TrackOverviewModalContainer = ({
  trackId,
  language: languageFromProps,
  allowFullWidthChildren,
  source
}) => {
  var _track$icon;
  const [[searchParamLanguage]] = useSearchParam(FILTER_TYPES.LANGUAGE);
  const {
    goTo
  } = useOverviewNavigation();
  const {
    isMobile
  } = useMediaQuery();
  const {
    trackLanguagesLoading: availableLanguagesLoading,
    trackLanguages: availableLanguagesData
  } = useAvailableLanguagesByTrack(trackId);
  const language = useMemo(() => {
    const chosenLanguage = (languageFromProps || searchParamLanguage || getLang()).toUpperCase();
    if (availableLanguagesData && availableLanguagesData.length && !availableLanguagesData.includes(chosenLanguage)) {
      // default to English if available
      return !availableLanguagesData.includes('EN') ? 'EN' : availableLanguagesData[0];
    }
    return chosenLanguage;
  }, [availableLanguagesData, languageFromProps, searchParamLanguage]);
  const {
    trackLoading: loading,
    track,
    trackError: error
  } = useFullTrack(Number(trackId), language);
  const deprecated = track && track.deprecated;
  const deprecationCta = track && track.deprecationCta;
  const trackAggregatedLessonContents = useMemo(() => {
    return track && track.lessons.reduce( /* eslint-disable-next-line hubspot-dev/no-reduce-accumulator-copy */
    (aggregated, lesson) => [...aggregated, ...lesson.content], []);
  }, [track]);
  const professors = useMemo(() => {
    if (!trackAggregatedLessonContents) return [];
    const professorsById = {};
    trackAggregatedLessonContents.forEach(asset => {
      if (asset.professors) {
        asset.professors.forEach(professor => {
          professorsById[professor.id] = professor;
        });
      }
    });
    return Object.values(professorsById);
  }, [trackAggregatedLessonContents]);
  const curriculumLessons = useMemo(() => {
    if (!track || !track.lessons.length) return [];
    return track.lessons.filter(lesson => !lesson.content.every(content => content.contentType === 'LINK'));
  }, [track]);
  const handleChangeSelectedLanguage = ({
    target: {
      value
    }
  }) => {
    UsageTracker.track('interaction', {
      action: 'overview-modal-change-language',
      appearsIn: source,
      trackId: track ? track.id : null,
      name: track ? track.name : null,
      previousLanguage: language,
      newLanguage: value
    });
    goTo({
      id: trackId,
      type: 'TRACK',
      language: value
    });
  };
  const handleStartLessonClick = (lesson, index) => () => {
    UsageTracker.track('interaction', {
      action: 'overview-modal-curriculum-click-start',
      contentType: 'LESSON',
      lessonId: lesson.id,
      lessonType: lesson.lessonType[0],
      appearsIn: 'course-overview-curriculum',
      position: index + 1,
      name: lesson.name,
      language
    });
  };
  const handleEnroll = () => {
    UsageTracker.track('interaction', {
      action: 'overview-modal-enroll',
      appearsIn: source,
      contentLanguage: language,
      contentType: 'TRACK',
      lessonId: track ? track.id : null,
      name: track ? track.name : null
    });
  };
  const handleOrganisationUrlClick = (professorId, organisation, organisationUrl) => () => {
    UsageTracker.track('interaction', {
      action: 'professor-organisation-click',
      contentType: 'COURSE',
      appearsIn: 'course-overview-curriculum',
      organisation,
      organisationUrl,
      professorId,
      trackId
    });
  };
  return /*#__PURE__*/_jsxs(OverviewContent, {
    allowFullWidthChildren: allowFullWidthChildren,
    assetLanguage: language,
    deprecated: !!deprecated,
    deprecationCta: deprecationCta || undefined,
    error: error,
    fallbackThumbnailUrl: track && track.previewThumbnailRectangleUrl,
    headerIcons: /*#__PURE__*/_jsx(CardMenuIcon, {
      item: {
        id: trackId,
        language,
        type: 'TRACK'
      },
      use: "on-dark"
    }),
    loading: loading || availableLanguagesLoading,
    trackId: trackId,
    children: [/*#__PURE__*/_jsx(OverviewModalDescriptionSection, {
      availableLanguages: availableLanguagesData,
      availableLanguagesLoading: availableLanguagesLoading,
      contentList: trackAggregatedLessonContents,
      description: track && track.description,
      duration: track && /*#__PURE__*/_jsxs(List, {
        inline: true,
        use: "inline-divided",
        wrapChildren: true,
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.overviewModal.numLessons",
          options: {
            number: track.lessons.length
          }
        }), /*#__PURE__*/_jsx(FormattedDuration, {
          from: new Date().getTime() - track.duration * 1000
        })]
      }),
      hasCertificate: track && (track.certification || !!track.exam),
      hasStudyGuide: track && !!track.studyGuide,
      id: track && track.id,
      onChangeSelectedLanguage: handleChangeSelectedLanguage,
      onEnroll: handleEnroll,
      selectedLanguage: language,
      title: track && track.name,
      type: "TRACK"
    }), track && (track.certification || !!track.exam) && /*#__PURE__*/_jsx(ACCertificatePreviewSection, {
      backgroundColor: "do-not-use-eerie",
      iconUrl: ((_track$icon = track.icon) === null || _track$icon === void 0 ? void 0 : _track$icon.location) || '',
      paddingInline: "700",
      title: track.name
    }), !!professors.length && /*#__PURE__*/_jsx(InstructorList, {
      handleOrganisationUrlClick: handleOrganisationUrlClick,
      language: language,
      padding: "800",
      professors: professors
    }), !!curriculumLessons.length && /*#__PURE__*/_jsx(OverviewModalCurriculumSection, {
      children: curriculumLessons.map((lesson, index) =>
      /*#__PURE__*/
      // @bend-expect trellis-tokenized
      _jsx(ExpandableSectionWrapper, {
        children: /*#__PURE__*/_jsx(ExpandableSection, {
          marginInlineStart: "-400",
          titleText: /*#__PURE__*/_jsx(View, {
            color: "text-core-default",
            children: /*#__PURE__*/_jsx(LearningContentCard, {
              disableLink: true,
              item: lessonToLessonCardModel(lesson),
              overviewType: "course-overview-curriculum",
              position: index + 1,
              renderMenu: () => null,
              size: "small",
              use: "on-surface-recessed"
            }, lesson.id)
          }),
          children: /*#__PURE__*/_jsxs(TFlex, {
            alignItems: "center",
            direction: isMobile ? 'column' : 'row',
            gap: 12,
            children: [/*#__PURE__*/_jsx(View, {
              flexGrow: 1,
              children: lesson.description
            }), /*#__PURE__*/_jsx(View, {
              children: /*#__PURE__*/_jsx(UIRouterButton, {
                "data-test-id": "start-lesson-button",
                onClick: handleStartLessonClick(lesson, index),
                role: "button",
                size: isMobile ? 'medium' : 'small',
                to: `/tracks/${trackId}/${lesson.id}?language=${language}`,
                use: "secondary",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "learningCenter.overviewModal.viewLesson"
                })
              })
            })]
          })
        })
      }, lesson.id))
    })]
  });
};
export default TrackOverviewModalContainer;